import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../contexts/AuthContext.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import RichTextEditor from "../components/RichTextEditor.js";
import AIEditor from "../components/AIEditor.js";
import "./styles/ArticlePortal.css";

const fmt = (s) => {
  if (s === null || s === undefined || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const VideoTrimmer = ({ videoUrl, initialStart, initialEnd, onTrimChange, token, onCoverChange, initialCover }) => {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(initialStart || 0);
  const [endTime, setEndTime] = useState(initialEnd || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [coverPreview, setCoverPreview] = useState(initialCover || null);
  const [coverUploading, setCoverUploading] = useState(false);

  const startTimeRef = useRef(startTime);
  const endTimeRef = useRef(endTime);
  const durationRef = useRef(duration);

  useEffect(() => { startTimeRef.current = startTime; }, [startTime]);
  useEffect(() => { endTimeRef.current = endTime; }, [endTime]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { setStartTime(initialStart || 0); }, [initialStart]);
  useEffect(() => { setCoverPreview(initialCover || null); }, [initialCover]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (isPreviewing && endTimeRef.current && video.currentTime >= endTimeRef.current) {
        video.pause();
        video.currentTime = startTimeRef.current;
        setIsPreviewing(false);
      }
    };
    video.addEventListener("timeupdate", onTimeUpdate);
    return () => video.removeEventListener("timeupdate", onTimeUpdate);
  }, [isPreviewing]);

  const handleLoadedMetadata = () => {
    const d = videoRef.current.duration;
    setDuration(d);
    const end = (initialEnd && initialEnd <= d) ? initialEnd : d;
    const start = (initialStart && initialStart < end) ? initialStart : 0;
    setEndTime(end);
    setStartTime(start);
    setCurrentTime(start);
    onTrimChange(start, end);
  };

  const startDragFn = (which, e) => {
    e.preventDefault();
    setDragging(which);
    const track = trackRef.current;
    if (!track) return;
    const onMove = (evt) => {
      evt.preventDefault();
      const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const time = pct * durationRef.current;
      if (which === "start") {
        const clamped = Math.min(time, endTimeRef.current - 0.25);
        setStartTime(clamped);
        startTimeRef.current = clamped;
        if (videoRef.current) videoRef.current.currentTime = clamped;
        onTrimChange(clamped, endTimeRef.current);
      } else {
        const clamped = Math.max(time, startTimeRef.current + 0.25);
        setEndTime(clamped);
        endTimeRef.current = clamped;
        if (videoRef.current) videoRef.current.currentTime = clamped;
        onTrimChange(startTimeRef.current, clamped);
      }
    };
    const onUp = () => {
      setDragging(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
  };

  const playPreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = startTime;
    video.play();
    setIsPreviewing(true);
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = startTime;
    setIsPreviewing(false);
  };

  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPreviewing(false);
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCoverPreview(dataUrl);
    setCoverUploading(true);
    canvas.toBlob(async (blob) => {
      try {
        const fd = new FormData();
        fd.append("file", blob, "cover.jpg");
        const resp = await fetch("/api/upload", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!resp.ok) {
          let msg = "Upload failed";
          try { const err = await resp.json(); msg = err.message || msg; } catch {}
          throw new Error(msg);
        }
        const data = await resp.json();
        onCoverChange(data.url);
      } catch (err) {
        console.error("Cover upload error:", err);
      } finally {
        setCoverUploading(false);
      }
    }, "image/jpeg", 0.92);
  };

  const discardCover = () => {
    setCoverPreview(null);
    onCoverChange(null);
  };

  const startPct = duration ? (startTime / duration) * 100 : 0;
  const endPct = duration ? (endTime / duration) * 100 : 100;
  const currentPct = duration ? (currentTime / duration) * 100 : 0;
  const markers = duration > 0
    ? Array.from({ length: 5 }, (_, i) => ({ time: (duration * i) / 4, pct: i * 25 }))
    : [];

  return (
    <div className="video-trimmer">
      <div className="trimmer-header">
        <span className="trimmer-title">✂ VIDEO EDITOR</span>
        <span className="trimmer-current-time">▶ {fmt(currentTime)} / {fmt(duration)}</span>
      </div>

      <video
        ref={videoRef}
        src={videoUrl}
        className="trimmer-preview"
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        playsInline
        controls
      />

      {duration > 0 && (
        <>
          <div className="trimmer-timeline">
            <div className="trimmer-track-wrapper" ref={trackRef}>
              <div className="trimmer-track-bg">
                <div className="trimmer-inactive" style={{ left: 0, width: `${startPct}%` }} />
                <div className="trimmer-active" style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }} />
                <div className="trimmer-inactive" style={{ left: `${endPct}%`, width: `${100 - endPct}%` }} />
              </div>
              <div className="trimmer-playhead" style={{ left: `${currentPct}%` }} />
              <div
                className={`trimmer-handle${dragging === "start" ? " dragging" : ""}`}
                style={{ left: `${startPct}%` }}
                onMouseDown={(e) => startDragFn("start", e)}
                onTouchStart={(e) => startDragFn("start", e)}
              >
                <div className="trimmer-handle-time">{fmt(startTime)}</div>
                <div className="trimmer-handle-bar">
                  <div className="trimmer-handle-grip" />
                  <div className="trimmer-handle-grip" />
                  <div className="trimmer-handle-grip" />
                </div>
              </div>
              <div
                className={`trimmer-handle${dragging === "end" ? " dragging" : ""}`}
                style={{ left: `${endPct}%` }}
                onMouseDown={(e) => startDragFn("end", e)}
                onTouchStart={(e) => startDragFn("end", e)}
              >
                <div className="trimmer-handle-time">{fmt(endTime)}</div>
                <div className="trimmer-handle-bar">
                  <div className="trimmer-handle-grip" />
                  <div className="trimmer-handle-grip" />
                  <div className="trimmer-handle-grip" />
                </div>
              </div>
            </div>
            <div className="trimmer-markers">
              {markers.map((m, i) => (
                <span key={i} className="trimmer-marker" style={{ left: `${m.pct}%` }}>
                  {fmt(m.time)}
                </span>
              ))}
            </div>
          </div>

          <div className="trimmer-info-row">
            <span className="trimmer-info-cell">
              <span className="trimmer-handle-dot start-dot" /> Start: <strong>{fmt(startTime)}</strong>
            </span>
            <span className="trimmer-info-cell trimmer-clip-len">
              Clip: <strong>{fmt(endTime - startTime)}</strong> &nbsp;/&nbsp; Total: {fmt(duration)}
            </span>
            <span className="trimmer-info-cell">
              End: <strong>{fmt(endTime)}</strong> <span className="trimmer-handle-dot end-dot" />
            </span>
          </div>

          <div className="trimmer-btn-row">
            {isPreviewing ? (
              <button type="button" className="trimmer-btn" onClick={stopPreview}>⏹ Stop Preview</button>
            ) : (
              <button type="button" className="trimmer-btn" onClick={playPreview}>▶ Preview Trim</button>
            )}
          </div>

          <div className="trimmer-cover-section">
            <div className="trimmer-cover-header">
              <span className="trimmer-cover-title">📷 COVER PHOTO</span>
              <span className="trimmer-cover-hint">Seek to any frame in the video above, then capture it as the cover image for this article</span>
            </div>
            <div className="trimmer-cover-body">
              <button
                type="button"
                className="trimmer-btn trimmer-capture-btn"
                onClick={captureFrame}
                disabled={coverUploading}
              >
                {coverUploading ? "Uploading..." : "📷 Capture Current Frame"}
              </button>
              {coverPreview && (
                <div className="trimmer-cover-preview">
                  <img src={coverPreview} alt="Cover preview" />
                  <button type="button" className="trimmer-cover-discard" onClick={discardCover} title="Remove cover">✕</button>
                </div>
              )}
            </div>
            {!coverPreview && (
              <p className="trimmer-cover-none">No cover set — video articles without a cover show a dark placeholder on article cards</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ArticlePortal = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editProcessed = useRef(false);
  const formRef = useRef(null);

  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const blankForm = {
    title: "", excerpt: "", content: "", image_url: "", media_type: "",
    category: "politics", subcategory_id: "", region_id: "",
    is_premium: false, byline: "",
    video_start_time: null, video_end_time: null, cover_image_url: "",
    scheduled: false, scheduled_at: "",
    media: [],
  };
  const [form, setForm] = useState(blankForm);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const categories = ["politics", "world", "business", "health", "technology", "entertainment", "sports", "local"];
  const categoriesWithSubs = ["entertainment", "sports", "business", "technology"];
  const [subcategories, setSubcategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState("All");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [postToX, setPostToX] = useState(false);
  const [xPostStatus, setXPostStatus] = useState(null);
  const [myName, setMyName] = useState("");

  useEffect(() => {
    fetch("/api/user/me", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setMyName(data.display_name || data.username || "");
      })
      .catch((err) => console.error("Error fetching current user:", err));
  }, [token]);

  useEffect(() => {
    if (form.category && categoriesWithSubs.includes(form.category)) {
      fetch(`/api/subcategories?category=${form.category}`)
        .then((res) => res.json())
        .then((data) => setSubcategories(data))
        .catch((err) => console.error("Error fetching subcategories:", err));
    } else {
      setSubcategories([]);
      setForm((f) => ({ ...f, subcategory_id: "" }));
    }
    if (form.category === "local") {
      fetch("/api/regions")
        .then((res) => res.json())
        .then((data) => setRegions(data))
        .catch((err) => console.error("Error fetching regions:", err));
    } else {
      setRegions([]);
      setForm((f) => ({ ...f, region_id: "" }));
    }
  }, [form.category]);

  const handleRoleChange = (e) => setSelectedRole(e.target.value);

  const fetchArticles = async () => {
    try {
      let url = "/api/journal/articles";
      if (user.role === "admin") url = `/api/admin/articles?role=${selectedRole}`;
      else if (user.role === "reporter") url = "/api/reporter/articles";
      const resp = await fetch(url, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Fetch error: ${resp.status}`);
      setArticles(await resp.json());
    } catch (err) {
      console.error(err);
      setError("Could not load articles");
    }
  };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchArticles();
  }, [token, user, selectedRole]);

  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || articles.length === 0 || editProcessed.current) return;
    const target = articles.find((a) => String(a.id) === String(editId));
    if (target) {
      editProcessed.current = true;
      handleEdit(target);
      setTimeout(() => {
        if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [articles]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    setError(null);
    const uploaded = [];
    try {
      for (const file of files) {
        console.log(`[Upload] Starting — name: "${file.name}", type: "${file.type}", size: ${file.size} bytes (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
        const formData = new FormData();
        formData.append("file", file);
        const resp = await fetch("/api/upload", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        console.log(`[Upload] Response — status: ${resp.status}, ok: ${resp.ok}`);
        if (!resp.ok) {
          let msg = resp.status === 413
            ? `"${file.name}" is too large. Videos must be under 200 MB. If uploading to the live site, ask your server admin to increase the Nginx upload limit.`
            : `Upload failed for "${file.name}" (HTTP ${resp.status})`;
          try { const err = await resp.json(); msg = err.message || msg; } catch (jsonErr) {
            console.warn("[Upload] Error response was not JSON:", jsonErr.message);
          }
          throw new Error(msg);
        }
        const data = await resp.json();
        console.log(`[Upload] Success — url: ${data.url}, storage: ${data.storage}, mimetype: ${data.mimetype}`);
        const isVideo = file.type.startsWith("video/") || data.mimetype?.startsWith("video/");
        uploaded.push({ url: data.url, type: isVideo ? "video" : "image" });
      }
      setForm((f) => {
        const hadFeatured = !!f.image_url;
        const newMedia = [...(f.media || []), ...uploaded];
        const featured = newMedia[0];
        return {
          ...f,
          media: newMedia,
          image_url: featured.url,
          media_type: featured.type,
          ...(hadFeatured ? {} : { video_start_time: null, video_end_time: null, cover_image_url: "" }),
        };
      });
      setShowTrimmer(false);
    } catch (err) {
      console.error("[Upload] Failed:", err);
      setUploadError(err.message || "Upload failed — check the browser console for details.");
    } finally {
      setUploading(false);
      input.value = "";
    }
  };

  const handleRemoveMedia = (index) => {
    setForm((f) => {
      const newMedia = (f.media || []).filter((_, i) => i !== index);
      if (newMedia.length === 0) {
        return {
          ...f,
          media: [],
          image_url: "",
          media_type: "",
          video_start_time: null,
          video_end_time: null,
          cover_image_url: "",
        };
      }
      const featured = newMedia[0];
      const featuredChanged = index === 0;
      return {
        ...f,
        media: newMedia,
        image_url: featured.url,
        media_type: featured.type,
        ...(featuredChanged ? { video_start_time: null, video_end_time: null, cover_image_url: "" } : {}),
      };
    });
    setShowTrimmer(false);
    setUploadError(null);
  };

  const getApiBase = () => {
    if (user.role === "admin") return "/api/admin";
    if (user.role === "reporter") return "/api/reporter";
    return "/api/journal";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setXPostStatus(null);
    const plainContent = (form.content || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    if (!plainContent) {
      setError("Content is required");
      return;
    }
    try {
      const apiBase = getApiBase();
      const resp = await fetch(
        editing ? `${apiBase}/articles/${editing.id}` : `${apiBase}/articles`,
        {
          method: editing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: form.title,
            excerpt: form.excerpt,
            content: form.content,
            image_url: form.image_url,
            media_type: form.media_type || "image",
            category: form.category,
            subcategory_id: form.subcategory_id || null,
            region_id: form.region_id || null,
            is_premium: form.is_premium,
            byline: user.role === "admin" ? form.byline : undefined,
            video_start_time: form.media_type === "video" ? (form.video_start_time ?? null) : null,
            video_end_time: form.media_type === "video" ? (form.video_end_time ?? null) : null,
            cover_image_url: form.media_type === "video" ? (form.cover_image_url || null) : null,
            media: form.media || [],
            scheduled_publish_at: form.scheduled && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
          }),
        }
      );
      if (!resp.ok) { const err = await resp.json(); throw new Error(err.message || "Save failed"); }
      const savedArticle = await resp.json();

      if (postToX) {
        setXPostStatus("posting");
        try {
          const xResp = await fetch("/api/post-to-x", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              title: form.title,
              excerpt: form.excerpt,
              articleId: savedArticle.id,
            }),
          });
          if (!xResp.ok) {
            const xErr = await xResp.json();
            setXPostStatus("error: " + (xErr.message || "Unknown error"));
          } else {
            setXPostStatus("success");
          }
        } catch (xErr) {
          setXPostStatus("error: " + xErr.message);
        }
      }

      setForm(blankForm);
      setEditing(null);
      setShowForm(false);
      setShowTrimmer(false);
      setPostToX(false);
      await fetchArticles();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  const handleEdit = (art) => {
    setEditing(art);
    setShowForm(true);
    setShowTrimmer(false);
    setPostToX(false);
    setXPostStatus(null);
    const mediaArr = Array.isArray(art.media) && art.media.length
      ? art.media
      : (art.image_url ? [{ url: art.image_url, type: art.media_type || "image" }] : []);
    setForm({
      title: art.title,
      excerpt: art.excerpt || "",
      content: art.content,
      image_url: art.image_url || (mediaArr[0] ? mediaArr[0].url : ""),
      media_type: art.media_type || (mediaArr[0] ? mediaArr[0].type : (art.image_url ? "image" : "")),
      media: mediaArr,
      category: art.type || "politics",
      subcategory_id: art.subcategory_id || "",
      region_id: art.region_id || "",
      is_premium: art.is_premium || false,
      byline: (user.role === "admin" && art.author_id !== user.id) ? "" : (art.byline || ""),
      video_start_time: art.video_start_time ?? null,
      video_end_time: art.video_end_time ?? null,
      cover_image_url: art.cover_image_url || "",
      scheduled: !!art.scheduled_publish_at,
      scheduled_at: art.scheduled_publish_at
        ? new Date(art.scheduled_publish_at).toISOString().slice(0, 16)
        : "",
    });
  };

  const handleDelete = async (art) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      let deleteUrl;
      if (user.role === "admin") deleteUrl = `/api/admin/articles/${art.id}`;
      else if (user.role === "reporter") deleteUrl = `/api/reporter/articles/${art.id}`;
      else deleteUrl = `/api/journal/articles/${art.id}`;
      const resp = await fetch(deleteUrl, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { const err = await resp.json(); throw new Error(`Delete failed: ${err.message}`); }
      await fetchArticles();
    } catch (err) {
      console.error(err);
      setError("Delete failed");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setShowTrimmer(false);
    setForm(blankForm);
    setPostToX(false);
    setXPostStatus(null);
  };

  return (
    <div className="article-portal">
      <h1>Article Portal</h1>
      {error && <div className="error">{error}</div>}

      <div className="role-filter">
        <label>Filter by role:</label>
        <select value={selectedRole} onChange={handleRoleChange}>
          <option value="All">All roles</option>
          <option value="admin">Admins</option>
          <option value="journalist">Journalists</option>
          <option value="reporter">Reporters</option>
        </select>
        <button
          className="create-article-btn"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
            setShowTrimmer(false);
            setForm(blankForm);
          }}
        >
          Create Article
        </button>
      </div>

      {(showForm || editing !== null) && (
        <div className="article-form" ref={formRef}>
          <h2>{editing ? "Edit Article" : "Create Article"}</h2>
          <form onSubmit={handleSubmit}>
            <div>
              <label>Title:</label>
              <input name="title" value={form.title} onChange={handleChange} required />
            </div>
            <div>
              <label>Excerpt:</label>
              <textarea name="excerpt" value={form.excerpt} onChange={handleChange} required />
            </div>
            <div>
              <label>Content:</label>
              {["admin", "journalist", "reporter"].includes(user.role) && (
                <AIEditor
                  token={token}
                  title={form.title}
                  contentHtml={form.content}
                  onApplyContent={(html) => setForm((f) => ({ ...f, content: html }))}
                  onInsertContent={(html) =>
                    setForm((f) => ({ ...f, content: (f.content || "") + html }))
                  }
                  onSetTitle={(t) => setForm((f) => ({ ...f, title: t }))}
                />
              )}
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                placeholder="Write your article here. Use the toolbar to format text and add links."
              />
            </div>
            <div>
              <label>Category:</label>
              <select name="category" value={form.category} onChange={handleChange} required>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            {subcategories.length > 0 && (
              <div>
                <label>Subcategory:</label>
                <select name="subcategory_id" value={form.subcategory_id} onChange={handleChange}>
                  <option value="">-- Select Subcategory (Optional) --</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>{sub.label}</option>
                  ))}
                </select>
              </div>
            )}
            {regions.length > 0 && (
              <div>
                <label>Region:</label>
                <select name="region_id" value={form.region_id} onChange={handleChange} required>
                  <option value="">-- Select Region --</option>
                  {regions.map((reg) => (
                    <option key={reg.id} value={reg.id}>{reg.name}, {reg.state}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label>Images/Videos:</label>
              <span className="media-hint">Add as many as you like. The first item is the featured cover used on the homepage and article cards; the rest appear in a gallery below the article.</span>
              <div className="file-upload-row">
                <input type="file" accept="image/*,video/*" multiple onChange={handleFileUpload} disabled={uploading} />
                {form.media_type === "video" && form.image_url && !uploading && (
                  <button
                    type="button"
                    className="edit-video-btn"
                    onClick={() => setShowTrimmer((s) => !s)}
                  >
                    ✂ {showTrimmer ? "Hide Editor" : "Edit Featured Video"}
                  </button>
                )}
              </div>
              {uploading && <span className="upload-status">Uploading… please wait</span>}
              {uploadError && (
                <div className="upload-error-inline">⚠ {uploadError}</div>
              )}

              {form.media && form.media.length > 0 && (
                <div className="media-thumb-grid">
                  {form.media.map((item, i) => (
                    <div className={`media-thumb-item${i === 0 ? " media-thumb-featured" : ""}`} key={`${item.url}-${i}`}>
                      {item.type === "video" ? (
                        <video className="media-thumb-media" src={item.url} muted preload="metadata" playsInline />
                      ) : (
                        <img className="media-thumb-media" src={item.url} alt={`Media ${i + 1}`} />
                      )}
                      {i === 0 && <span className="media-thumb-badge">Featured</span>}
                      {item.type === "video" && <span className="media-thumb-type">▶ Video</span>}
                      <button
                        type="button"
                        className="media-thumb-remove"
                        onClick={() => handleRemoveMedia(i)}
                        title="Remove this item"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {form.media_type === "video" && form.image_url && (
                <>
                  {showTrimmer && (
                    <VideoTrimmer
                      videoUrl={form.image_url}
                      initialStart={form.video_start_time}
                      initialEnd={form.video_end_time}
                      initialCover={form.cover_image_url || null}
                      token={token}
                      onTrimChange={(start, end) =>
                        setForm((f) => ({ ...f, video_start_time: start, video_end_time: end }))
                      }
                      onCoverChange={(url) =>
                        setForm((f) => ({ ...f, cover_image_url: url || "" }))
                      }
                    />
                  )}
                  {!showTrimmer && (form.video_start_time !== null || form.cover_image_url) && (
                    <div className="video-summary">
                      {form.video_start_time !== null && (
                        <span>✂ Trim: {fmt(form.video_start_time)} – {fmt(form.video_end_time)}</span>
                      )}
                      {form.cover_image_url && (
                        <img src={form.cover_image_url} className="cover-thumb-small" alt="Cover" />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="premium-checkbox">
              <label className="premium-label">
                <input type="checkbox" name="is_premium" checked={form.is_premium} onChange={handleChange} />
                <span className="premium-checkbox-text">Premium Article</span>
                <span className="premium-hint">Only paying subscribers can read the full article</span>
              </label>
            </div>

            <div className="premium-checkbox schedule-checkbox">
              <label className="premium-label">
                <input type="checkbox" name="scheduled" checked={form.scheduled} onChange={handleChange} />
                <span className="premium-checkbox-text">Schedule for later</span>
                <span className="premium-hint">Article will be held and published automatically at the chosen time</span>
              </label>
              {form.scheduled && (
                <div className="schedule-datetime-row">
                  <input
                    type="datetime-local"
                    name="scheduled_at"
                    value={form.scheduled_at}
                    min={(() => { const d = new Date(Date.now() + 60000); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); })()}
                    onChange={handleChange}
                    required={form.scheduled}
                    className="schedule-datetime-input"
                  />
                  <span className="schedule-tz-hint">Your local time</span>
                </div>
              )}
            </div>

            <div className="premium-checkbox post-to-x-checkbox">
              <label className="premium-label">
                <input
                  type="checkbox"
                  checked={postToX}
                  onChange={(e) => { setPostToX(e.target.checked); setXPostStatus(null); }}
                />
                <span className="premium-checkbox-text">Post to X / Twitter</span>
                <span className="premium-hint">Automatically publish this article to your X account when saved</span>
              </label>
            </div>
            {xPostStatus && (
              <div className={`x-post-status ${xPostStatus === "success" ? "x-post-success" : xPostStatus === "posting" ? "x-post-posting" : "x-post-error"}`}>
                {xPostStatus === "posting" && "⏳ Posting to X…"}
                {xPostStatus === "success" && "✓ Posted to X successfully!"}
                {xPostStatus.startsWith("error:") && `⚠ X post failed: ${xPostStatus.slice(7)}`}
              </div>
            )}
            {user.role === "admin" && (
              <div className="publish-as-field">
                <label>Publish As:</label>
                <select name="byline" value={form.byline} onChange={handleChange}>
                  <option value="">{myName ? `${myName} (default)` : "— Author's Name (default) —"}</option>
                  <option value="Anonymous">Anonymous</option>
                  <option value="VyralMediaGroup">VyralMediaGroup</option>
                </select>
              </div>
            )}
            <div className="form-buttons">
              <button type="submit">
                {editing
                  ? (form.scheduled ? "Update & Schedule" : "Update")
                  : (form.scheduled ? "Schedule Article" : "Publish Now")}
              </button>
              <button type="button" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="article-list">
        <h2>Existing Articles</h2>
        {articles.length === 0 ? (
          <p>No articles yet.</p>
        ) : (
          <ul>
            {articles.map((art) => (
              <li key={art.id} className={art.scheduled_publish_at && !art.is_published ? "article-scheduled" : ""}>
                <h3>
                  {art.title}{" "}
                  {art.is_premium && <span className="premium-badge">PREMIUM</span>}
                  {art.media_type === "video" && <span className="video-article-badge">VIDEO</span>}
                  {art.scheduled_publish_at && !art.is_published && (
                    <span className="scheduled-badge" title={new Date(art.scheduled_publish_at).toLocaleString()}>
                      ⏰ SCHEDULED {new Date(art.scheduled_publish_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </h3>
                <small className="article-author">By {art.author_name || "Unknown"}</small>
                <p>{art.excerpt}</p>
                <div className="actions">
                  {(user.role === "admin" || art.author_id === user.id) && (
                    <>
                      <button onClick={() => handleEdit(art)}>Edit</button>
                      <button onClick={() => handleDelete(art)}>Delete</button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ArticlePortal;
